import { ActiveRuleset } from "./activeRuleset";

/**
 * The Training Range: every visitor can open it from the menu, and each one gets a private range of their own
 * (no other players, no bots): racks with every weapon that drops on Fargo, infinite ammo, and practice dummies
 * that can't die. One training game hosts several ranges ("slots") laid out in a grid, far enough apart that
 * nothing (sight, bullets, grenades) reaches from one range into another.
 */
export const TrainingLayout = Object.freeze({
    /** one private range, in game units: lanes run left to right from the firing line */
    slotWidth: 260,
    slotHeight: 200,
    /**
     * space between ranges: more than the longest bullet range (275) and the widest scope view,
     * so a trainee can never see or hit a neighbouring range
     */
    /** water between ranges (the map has to fit in the largest position the network can send: 1924) */
    gap: 150,
    columns: 4,
    rows: 3,
    /** where a trainee appears, and the firing line they shoot from (relative to the range's corner) */
    spawn: { x: 30, y: 100 },
    firingLineX: 44,
    /** a trainee can walk anywhere inside their range, a little in from the edge */
    margin: 4,
    /** the main lane: dummies at these distances (units) from the firing line */
    laneY: 100,
    laneDistances: [10, 25, 50, 80, 120, 180] as readonly number[],
    /** extra dummies beside the main lane (distance, sideways offset), for judging spread */
    sideDummies: [[25, -18], [25, 18], [50, -22], [50, 22]] as ReadonlyArray<readonly [number, number]>,
    /** racks: guns above the spawn, melee / throwables / scopes / armour below it */
    rackColumns: 7,
    rackSpacing: 6,
    rackX: 6,
    gunRackY: 72,
    kitRackY: 110,
    /** a trainee who hasn't moved, aimed or fired for this long is sent back to the menu (frees the range) */
    idleKickMs: 5 * 60_000,
    /**
     * the practice yard, below the lane: barrels, boxes and a crate to try the Gravity Gloves on (they hang on the
     * kit rack): grab, swing, merge (V) and throw them at the dummies. Put back as new by every reset.
     */
    gravityYard: [
        { idString: "barrel", x: 62, y: 158 },
        { idString: "barrel", x: 75, y: 158 },
        { idString: "barrel", x: 88, y: 158 },
        { idString: "wine_barrel", x: 101, y: 158 },
        { idString: "propane_tank", x: 112, y: 158 },
        { idString: "box", x: 62, y: 174 },
        { idString: "box", x: 72, y: 174 },
        { idString: "regular_crate", x: 88, y: 176 },
        { idString: "barrel", x: 104, y: 176 }
    ] as ReadonlyArray<{ readonly idString: string, readonly x: number, readonly y: number }>
});

export const trainingMapSize = (): { readonly width: number, readonly height: number } => ({
    width: TrainingLayout.gap + TrainingLayout.columns * (TrainingLayout.slotWidth + TrainingLayout.gap),
    height: TrainingLayout.gap + TrainingLayout.rows * (TrainingLayout.slotHeight + TrainingLayout.gap)
});

/** the top-left corner of a range, by its index */
export const trainingSlotOrigin = (index: number): { readonly x: number, readonly y: number } => ({
    x: TrainingLayout.gap + (index % TrainingLayout.columns) * (TrainingLayout.slotWidth + TrainingLayout.gap),
    y: TrainingLayout.gap + Math.floor(index / TrainingLayout.columns) * (TrainingLayout.slotHeight + TrainingLayout.gap)
});

/** which range a world position is in (the client uses it to draw lane markings), or -1 */
export const trainingSlotAt = (x: number, y: number): number => {
    const pitchX = TrainingLayout.slotWidth + TrainingLayout.gap, pitchY = TrainingLayout.slotHeight + TrainingLayout.gap;
    const col = Math.floor((x - TrainingLayout.gap) / pitchX), row = Math.floor((y - TrainingLayout.gap) / pitchY);
    if (col < 0 || row < 0 || col >= TrainingLayout.columns || row >= TrainingLayout.rows) return -1;
    const localX = x - TrainingLayout.gap - col * pitchX, localY = y - TrainingLayout.gap - row * pitchY;
    if (localX < 0 || localY < 0 || localX > TrainingLayout.slotWidth || localY > TrainingLayout.slotHeight) return -1;
    return row * TrainingLayout.columns + col;
};

export const TRAINING_SLOT_COUNT = TrainingLayout.columns * TrainingLayout.rows;

/** how far a shot may go past the edge of its own range (a little way into the water round it) before it stops */
export const TRAINING_SHOT_MARGIN = 40;

/** where a shot fired in a range stops: the range and a little of the water round it (never another range) */
export const trainingShotBounds = (index: number): { readonly min: { readonly x: number, readonly y: number }, readonly max: { readonly x: number, readonly y: number } } => {
    const origin = trainingSlotOrigin(index);
    return {
        min: { x: origin.x - TRAINING_SHOT_MARGIN, y: origin.y - TRAINING_SHOT_MARGIN },
        max: { x: origin.x + TrainingLayout.slotWidth + TRAINING_SHOT_MARGIN, y: origin.y + TrainingLayout.slotHeight + TRAINING_SHOT_MARGIN }
    };
};

/** everything laid out on the racks: all guns that drop on Fargo (the roster, then the older ones still found there) */
export const TrainingLoadout = Object.freeze({
    guns: [...ActiveRuleset.guns, "hp18", "badlander", "vepr12", "mp153"] as readonly string[],
    kit: [
        ...ActiveRuleset.melee,
        ...ActiveRuleset.throwables,
        "gas_grenade",
        // (no 8x or 16x: out of the game, ActiveRuleset.retiredItems; the 6x that took the 8x's place is rare loot)
        "4x_scope", "6x_scope",
        "basic_helmet", "regular_helmet", "tactical_helmet",
        "basic_vest", "regular_vest", "tactical_vest",
        "gas_mask"
    ] as readonly string[]
});

/** where each dummy of a range stands (relative to the range's corner) */
export const trainingDummySpots = (): ReadonlyArray<{ readonly x: number, readonly y: number, readonly distance: number }> => [
    ...TrainingLayout.laneDistances.map(distance => ({ x: TrainingLayout.firingLineX + distance, y: TrainingLayout.laneY, distance })),
    ...TrainingLayout.sideDummies.map(([distance, offset]) => ({ x: TrainingLayout.firingLineX + distance, y: TrainingLayout.laneY + offset, distance }))
];

/** a rack position (relative to the range's corner) */
export const trainingRackSpot = (rackY: number, index: number): { readonly x: number, readonly y: number } => ({
    x: TrainingLayout.rackX + (index % TrainingLayout.rackColumns) * TrainingLayout.rackSpacing,
    y: rackY + Math.floor(index / TrainingLayout.rackColumns) * (TrainingLayout.rackSpacing + 1)
});

/** settings panel commands (sent with InputActions.TrainingCommand as one byte: command | value << 4) */
export const enum TrainingCommand {
    /** value 0..3: no armour, then level 1..3 helmet and vest */
    DummyArmor,
    /** value 0 or 1: dummies stand still or strafe */
    DummyMoving,
    /** reset the room: racks restocked in place, the floor cleared (loot, fire, gas, smoke, grenades, scorch marks), dummies home */
    Reset,
    /** the dummy settings back to the defaults: no armour, standing still */
    ResetSettings
}

export const encodeTrainingCommand = (command: TrainingCommand, value = 0): number => (command & 0x0f) | ((value & 0x0f) << 4);
export const decodeTrainingCommand = (byte: number): { readonly command: TrainingCommand, readonly value: number } => ({ command: (byte & 0x0f) as TrainingCommand, value: (byte >> 4) & 0x0f });

/** the map name the training game runs */
export const TRAINING_MAP = "training";
